// CARDS v3 — four card stats (0–3) with icons + pips, in printed order:
// Absorption, Shading, Storm protection | Population.

const Drop = ({ s, c }) => (
  <svg width={s} height={s} viewBox="0 0 14 14" aria-hidden="true">
    <path d="M7 1.2 C 5 4.4, 3 6.6, 3 8.9 A 4 4 0 0 0 11 8.9 C 11 6.6, 9 4.4, 7 1.2 Z" fill={c} />
  </svg>
);
const Tree = ({ s, c }) => (
  <svg width={s} height={s} viewBox="0 0 14 14" aria-hidden="true">
    <circle cx="7" cy="5.4" r="4.2" fill={c} />
    <rect x="6.2" y="8.6" width="1.6" height="4.4" rx="0.8" fill={c} />
  </svg>
);
const Shield = ({ s, c }) => (
  <svg width={s} height={s} viewBox="0 0 14 14" aria-hidden="true">
    <path d="M7 1.2 L12 3 V7 C 12 10, 9.8 12.2, 7 13 C 4.2 12.2, 2 10, 2 7 V3 Z" fill={c} />
  </svg>
);
const Person = ({ s, c }) => (
  <svg width={s} height={s} viewBox="0 0 14 14" aria-hidden="true">
    <circle cx="7" cy="4" r="2.6" fill={c} />
    <path d="M2 13 C 2 9.4, 4.2 7.6, 7 7.6 C 9.8 7.6, 12 9.4, 12 13 Z" fill={c} />
  </svg>
);

export const STAT_META = [
  { key: "absorb", label: "Absorption", color: "#3A8FB0", Icon: Drop },
  { key: "shade", label: "Shading", color: "#6E9F45", Icon: Tree },
  { key: "storm", label: "Storm protection", color: "#2F7466", Icon: Shield },
  { key: "pop", label: "Population", color: "#8C857A", Icon: Person },
];
const PIP_OFF = "#DDD6CB";
const MAX = 3;

const perFloor = (card) => card?.key === "house_cube";

function Pips({ n, color, w, h, gap }) {
  return (
    <span className="inline-flex" style={{ gap }}>
      {Array.from({ length: MAX }).map((_, i) => (
        <span key={i} style={{ width: w, height: h, borderRadius: h, background: i < n ? color : PIP_OFF }} />
      ))}
    </span>
  );
}

export const statLine = (card) =>
  STAT_META.filter((m) => (card?.stats?.[m.key] || 0) > 0)
    .map((m) => `${m.label} ${card.stats[m.key]}${m.key === "pop" && perFloor(card) ? " per cube" : ""}`)
    .join(", ");

// Mini cards (hand / market): non-zero stats only, icon + pips. House Cube: "Population 1 per cube".
export function CompactStats({ card, testid, wrap = true }) {
  if (card?.kind !== "build") return <span data-testid={testid}>Action card</span>;
  const stats = card.stats || {};
  if (perFloor(card)) {
    const m = STAT_META[3];
    return (
      <span data-testid={testid} title={statLine(card)} className="inline-flex items-center gap-1">
        <m.Icon s={10} c={m.color} />
        <span>Population {stats.pop ?? 1} per cube</span>
      </span>
    );
  }
  const shown = STAT_META.filter((m) => (stats[m.key] || 0) > 0);
  return (
    <span data-testid={testid} title={statLine(card) || "No stats"} aria-label={statLine(card) || "No stats"} className={`inline-flex items-center ${wrap ? "flex-wrap" : "flex-nowrap overflow-hidden"}`} style={{ columnGap: 5, rowGap: 2 }}>
      {shown.length === 0 && <span>No stats</span>}
      {shown.map((m) => (
        <span key={m.key} data-testid={testid ? `${testid}-${m.key}` : undefined} data-value={stats[m.key]} className="inline-flex items-center shrink-0" style={{ gap: 2 }}>
          <m.Icon s={10} c={m.color} />
          <Pips n={stats[m.key]} color={m.color} w={3} h={3} gap={1} />
        </span>
      ))}
    </span>
  );
}

// Card zoom / fallback card: all four stats with icon, pips and label (weather stats | population).
export function FullStats({ card, testid = "card-stats", dense = false }) {
  if (card?.kind !== "build") return null;
  const stats = card.stats || {};
  const cell = (m) => (
    <span key={m.key} data-testid={`${testid}-${m.key}`} data-value={stats[m.key] || 0} className="flex flex-col items-center" style={{ gap: dense ? 2 : 4, width: dense ? 30 : 42 }}>
      <m.Icon s={dense ? 12 : 18} c={(stats[m.key] || 0) > 0 ? m.color : PIP_OFF} />
      <Pips n={stats[m.key] || 0} color={m.color} w={dense ? 5 : 9} h={dense ? 3 : 4} gap={dense ? 1.5 : 2} />
      {!dense && (
        <span style={{ fontSize: 9.5, lineHeight: "11px", color: "var(--ink-4)", textAlign: "center" }}>
          {m.label}
          {m.key === "pop" && perFloor(card) ? " per cube" : ""}
        </span>
      )}
    </span>
  );
  return (
    <div data-testid={testid} aria-label={statLine(card)} className="flex items-start justify-between w-full">
      <span className="flex" style={{ gap: 2 }}>{STAT_META.slice(0, 3).map(cell)}</span>
      <span aria-hidden="true" style={{ width: 1, alignSelf: "stretch", background: "var(--line)" }} />
      <span className="flex" style={{ gap: 2 }}>{STAT_META.slice(3).map(cell)}</span>
    </div>
  );
}
